import { RootState, ThunkDispatch } from "core-integration/src/store";
import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import {
  getH2HLeague,
  getH2HNewEntries,
  getH2HStandings,
} from "core-integration/src/store/leagues/reducers";
import { fetchH2HLeagueStandings } from "core-integration/src/store/leagues/thunks";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { useLocalizedNavigate } from "plos/src/hooks/useLocalizedNavigate";
import { rightSidebarLayout } from "plos/src/layouts";
import qs from "qs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router";
import { useTrackingContext } from "../../../../contexts/TrackingContext";
import { HelmetHead } from "../../../HelmetHead";
import ReportNameButton from "../../../ReportNameButton";
import { reportButtonWrapper, StandingsHeading } from "../../shared";
import { H2HNav } from "../H2HNav";
import { standingsH2HMainContent } from "./standingsH2H.css";
import { StandingsH2HTable } from "./StandingsH2HTable";

const StandingsH2H = () => {
  const { t } = useTranslation("leaguesAndCups");
  const dispatch = useDispatch<ThunkDispatch>();
  const { leagueId } = useParams();
  const navigate = useLocalizedNavigate();
  const { search } = useLocation();

  const { firePageViewEvent } = useTrackingContext();

  const leagueNumber = parseInt(leagueId!, 10) || 0;
  const query = qs.parse(search, {
    ignoreQueryPrefix: true,
  });
  const defaultEntryNumber = parseInt(query.default_entry as string, 10) || 0;
  const pageNewEntries = parseInt(query.page_new_entries as string, 10) || 1;
  const pageStandings = parseInt(query.page_standings as string, 10) || 1;
  const now = useSelector(getCurrentEvent);

  const league = useSelector((state: RootState) =>
    getH2HLeague(state, leagueNumber),
  );

  const standings = useSelector((state: RootState) =>
    getH2HStandings(state, leagueNumber, pageStandings),
  );

  const newEntries = useSelector((state: RootState) =>
    getH2HNewEntries(state, leagueNumber, 1),
  );

  useEffect(() => {
    firePageViewEvent("fantasy leagues & cups", "standings head to head");
  }, []);

  useEffect(() => {
    const fetchLeagueStandings = () => {
      if (leagueNumber) {
        dispatch(
          fetchH2HLeagueStandings(leagueNumber, pageNewEntries, pageStandings),
        );
      }
    };
    fetchLeagueStandings();
  }, [dispatch, leagueNumber, pageNewEntries, pageStandings]);

  if (!league) {
    return null;
  }

  if (!now || newEntries?.results.length) {
    navigate(`/leagues/${league.id}/new-entries/h`, { replace: true });
  }

  return (
    <>
      <HelmetHead
        title={t(
          "standingsH2H.metadata.title",
          "{{leagueName}} - Head-to-Head League Standings | Fantasy Premier League",
          { leagueName: league.name },
        )}
        description={t(
          "standingsH2H.metadata.description",
          "To view the {{leagueName}} invitational head-to-head league standings, as well as creating & joining new leagues, visit the official website of the Premier League.",
          { leagueName: league.name },
        )}
      />
      <div className={rightSidebarLayout}>
        <SurfaceContainer>
          <StandingsHeading
            type="h2h"
            league={league}
            leagueNumber={leagueNumber}
          />
          <div className={standingsH2HMainContent}>
            <H2HNav view="standings" defaultEntry={defaultEntryNumber} />
            <StandingsH2HTable standings={standings} eventId={now?.id} />
            <div className={reportButtonWrapper.mobile}>
              <ReportNameButton league={league} />
            </div>
          </div>
        </SurfaceContainer>
      </div>
    </>
  );
};

export default StandingsH2H;
